// Template list + lazy file load. Every screen that needs templates uses this
// hook — components never import the api module directly.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearTemplateListCache,
  fetchTemplateFileHtml,
  fetchTemplatesCatalog,
  fetchTemplatesForRadiologyOrder,
  fetchTemplatesForService,
} from "../api/radiologyReportApi";
import { canonicalModality } from "../radiologyOrdersShared";
import { htmlIsEmpty } from "../utils/radiologyPlaceholders";

const norm = (s) => String(s || "").trim().toLowerCase();

const normModality = (s) => norm(canonicalModality(s));

export function templatesForOrder(templates, order, keepTemplateId = "") {
  if (!templates?.length) return [];
  const modality = normModality(order?.modality);
  if (!modality || modality === "-") return templates;
  return templates.filter((t) => {
    const templateModality = normModality(t.modality);
    if (!templateModality) return true;
    if (templateModality === modality) return true;
    return Boolean(keepTemplateId) && t.template_id === keepTemplateId;
  });
}

export function suggestTemplate(templates, order) {
  if (!templates?.length || !order) return null;
  const service = norm(order.service);
  const modality = normModality(order.modality);

  let best = null;
  let bestScore = 0;
  templates.forEach((t) => {
    if (t.is_active === false) return;
    let score = 0;
    const services = (t.service_names || []).map(norm).filter(Boolean);
    if (services.some((s) => s === service)) score += 100;
    else if (service && services.some((s) => service.includes(s) || s.includes(service))) score += 55;
    if (modality && normModality(t.modality) === modality) score += 30;
    if (score > bestScore) {
      best = t;
      bestScore = score;
    }
  });
  return best;
}

/**
 * `order`             → try every service id alias for this study.
 * `medicalServiceId`  → get_radiology_report_templates/ for one investigation.
 * `catalog`           → same call for every investigation this org has saved.
 */
export default function useRadiologyTemplates({
  activeOnly = false,
  medicalServiceId = null,
  catalog = false,
  order = null,
} = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hydratingId, setHydratingId] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const orderKey = order
    ? [
        order.orderNo,
        order.medicalServiceId,
        order.medicalServiceNo,
        order.service,
      ].join("|")
    : "";
  const orderRef = useRef(order);
  orderRef.current = order;

  const reload = useCallback(async ({ bustCache = false } = {}) => {
    if (bustCache) clearTemplateListCache();
    setLoading(true);
    setError("");
    try {
      let list = [];
      const currentOrder = orderRef.current;
      if (currentOrder) {
        list = await fetchTemplatesForRadiologyOrder(currentOrder);
      } else if (medicalServiceId) {
        list = await fetchTemplatesForService(medicalServiceId);
      } else if (catalog) {
        list = await fetchTemplatesCatalog();
      }
      if (mounted.current) setTemplates(list);
      return list;
    } catch (e) {
      if (mounted.current) {
        setTemplates([]);
        setError(e?.response?.data?.message || e?.message || "Could not load templates.");
      }
      return [];
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [medicalServiceId, catalog, orderKey]);

  useEffect(() => {
    reload();
  }, [reload]);

  const hydrateTemplateContent = useCallback(async (template) => {
    if (!template?.template_id || !htmlIsEmpty(template.content_html)) return template;
    setHydratingId(template.template_id);
    try {
      const html = await fetchTemplateFileHtml({
        templateId: template.template_id,
        medicalServiceId:
          template.medical_service_id ||
          order?.medicalServiceId ||
          medicalServiceId ||
          "",
      });
      const hydrated = { ...template, content_html: html };
      if (mounted.current) {
        setTemplates((prev) =>
          prev.map((t) =>
            t.template_id === hydrated.template_id ? hydrated : t,
          ),
        );
      }
      return hydrated;
    } finally {
      if (mounted.current) setHydratingId("");
    }
  }, [medicalServiceId, order?.medicalServiceId]);

  const visible = useMemo(
    () => (activeOnly ? templates.filter((t) => t.is_active !== false) : templates),
    [templates, activeOnly],
  );

  return {
    templates: visible,
    loading,
    error,
    hydratingId,
    reload,
    hydrateTemplateContent,
  };
}
